import { useVirtualizer } from '@tanstack/react-virtual';
import { FileEdit, FileText, Folder, Info, Upload, X } from 'lucide-react';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FolderPicker } from '@/components/folders/FolderPicker';
import { IManageFilePicker } from '@/components/imanage/IManageFilePicker';
import { NetDocsFilePicker } from '@/components/netdocs/NetDocsFilePicker';
import { FileUploadDropArea } from '@/components/shared/FileUploadDropArea';
import { TokenLimitTooltip } from '@/components/shared/TokenLimitTooltip';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from "@/components/ui/label";
import { Button as NewButton } from '@/components/ui/NewButton';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  IMAGE_FILE_EXTENSIONS,
  POWERPOINT_FILE_EXTENSIONS,
  SPREADSHEET_FILE_EXTENSIONS,
  SUPPORTED_UPLOAD_FILE_TYPES_STRING,
  TEXT_FILE_EXTENSIONS,
  WORD_FILE_EXTENSIONS,
} from '@/constants/supported-file-types';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import { useFolderTree } from '@/hooks/useFolders';
import { useNetDocsStatus } from '@/hooks/useNetDocsStatus';
import { useTokenLimit } from '@/hooks/useTokenLimit';
import { backendClient } from '@/lib/api-client';
import { processDroppedDataTransfer } from '@/lib/dragDropUtils';
import { SUPPORTED_FILE_EXTENSIONS, shouldFilterFile } from '@/lib/fileFilterUtils';
import {
  imanagePickerUploadFilename,
  joinIManageDocumentFilename,
  type IManagePickerFile,
} from '@/lib/imanagePickerFilename';
import { buildDisplayPathFromTree, getFileDestinationPath as getFileDestinationPathUtil } from '@/lib/folderPathUtils';
import { toolbarActionLabelClass } from '@/lib/toolbar-compact';
import { orchestrateUpload } from '@/lib/uploadOrchestrator';
import { extractZipFile, isZipFile } from '@/lib/zipUtils';
import Loading from './Loading';

interface FileUploadProps {
  matterGuid: string;
  onUploadComplete?: (uploadedFileNames?: string[]) => void;
  disabled?: boolean;
  buttonText?: string;
  /** Tooltip shown on hover — useful when the button is icon-only (empty buttonText). */
  buttonTooltip?: string;
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  acceptTypes?: string;
  multiple?: boolean;
  showAsDialog?: boolean;
  dialogTriggerComponent?: React.ReactNode;
  className?: string;
  onProcessingStateChange?: (isProcessing: boolean) => void;
  documentType?: string | null; // Optional document type to assign to all uploaded files
  isDraft?: boolean; // Optional draft status to assign to all uploaded files
  // Customization props for different contexts (e.g., bundle upload)
  hideSelectFolderButton?: boolean; // Hide the "Select Folder" button
  selectFilesButtonText?: string; // Custom text for "Select Files" button
  hideOptions?: boolean; // Hide draft option
  hideIManageImport?: boolean; // Hide iManage import button
  supportedTypesText?: string; // Custom text for supported file types
  dialogTitle?: string; // Custom dialog title
  dropZoneText?: string; // Custom drop zone text
  isLoading?: boolean; // External loading state for trigger button
}

export interface FileUploadHandles {
  openDialog: () => void;
  openWithFiles: (files: File[], folderId?: string) => Promise<void>;
}

export const DEFAULT_SUPPORTED_TYPES_TEXT = 'Supports PDF, Word, PowerPoint, text, Excel, images, email, and ZIP files';

const formatAcceptedExtensions = (acceptTypes?: string) => {
  const extensions = (acceptTypes || SUPPORTED_FILE_EXTENSIONS.join(','))
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter((type) => type.startsWith('.'));

  return Array.from(new Set(extensions));
};

const EXTENSION_GROUPS = [
  { label: 'PDF', extensions: ['.pdf'] },
  { label: 'Word', extensions: [...WORD_FILE_EXTENSIONS] },
  { label: 'PowerPoint', extensions: [...POWERPOINT_FILE_EXTENSIONS] },
  { label: 'Text', extensions: [...TEXT_FILE_EXTENSIONS] },
  { label: 'Excel', extensions: [...SPREADSHEET_FILE_EXTENSIONS] },
  { label: 'Image', extensions: [...IMAGE_FILE_EXTENSIONS] },
  { label: 'Email', extensions: ['.eml', '.msg'] },
  { label: 'Archive', extensions: ['.zip'] },
] as const;

type ExtensionGroup = {
  label: string;
  extensions: string[];
};

export function SupportedFileTypesText({
  text = DEFAULT_SUPPORTED_TYPES_TEXT,
  acceptTypes,
  className = '',
}: {
  text?: string;
  acceptTypes?: string;
  className?: string;
}) {
  const extensionGroups = useMemo(() => {
    const acceptedExtensions = formatAcceptedExtensions(acceptTypes);
    const acceptedSet = new Set(acceptedExtensions);
    const groupedExtensions = new Set<string>();

    const groups: ExtensionGroup[] = EXTENSION_GROUPS.map((group) => {
      const extensions = group.extensions.filter((extension) => acceptedSet.has(extension));
      extensions.forEach((extension) => groupedExtensions.add(extension));
      return { ...group, extensions };
    }).filter((group) => group.extensions.length > 0);

    const otherExtensions = acceptedExtensions.filter((extension) => !groupedExtensions.has(extension));
    if (otherExtensions.length > 0) {
      groups.push({ label: 'Other', extensions: otherExtensions });
    }

    return groups;
  }, [acceptTypes]);

  return (
    <p className={className}>
      <span>{text}</span>
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              aria-label="Show supported file extensions"
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              className="ml-1 inline-flex h-4 w-4 items-center justify-center align-[-2px] text-slate-400 hover:text-slate-600"
            >
              <Info className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="pointer-events-none w-[260px] px-3 py-2 text-xs">
            <div className="mb-2 font-medium text-slate-800">Supported extensions</div>
            <div className="space-y-1.5">
              {extensionGroups.map((group) => (
                <div key={group.label} className="grid grid-cols-[54px_1fr] items-start gap-2">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                    {group.label}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {group.extensions.map((extension) => (
                      <span
                        key={extension}
                        className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono text-[11px] leading-4 text-slate-700"
                      >
                        {extension}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </p>
  );
}

// Virtualized file list component for performance with large file counts
const VirtualizedFileList = React.memo(({
  files,
  getFileDestinationPath,
  removeFile,
  isUploading,
  isDownloadingFromIManage
}: {
  files: File[];
  getFileDestinationPath: (file: File) => string | null;
  removeFile: (index: number) => void;
  isUploading: boolean;
  isDownloadingFromIManage: boolean;
}) => {
  const parentRef = React.useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: files.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 5,
  });

  return (
    <div
      ref={parentRef}
      className="h-[clamp(6rem,22dvh,12.5rem)] overflow-y-auto"
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize() + 16}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        <div style={{ padding: '8px' }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const file = files[virtualRow.index];
            const destinationPath = getFileDestinationPath(file);

            return (
              <div
                key={virtualRow.key}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: `${virtualRow.size}px`,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
              <div
                className="flex items-center text-sm px-1.5 py-1 bg-slate-50 rounded-sm group h-full"
                title={file.name}
              >
                <FileText className="h-4 w-4 mr-2 text-slate-500 shrink-0" />
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="truncate w-full font-medium">
                    {file.name.length > 40
                      ? file.name.substring(0, 25) + '...' + file.name.substring(file.name.lastIndexOf('.'))
                      : file.name
                    }
                  </span>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span>{(file.size / 1024).toFixed(0)} KB</span>
                    {destinationPath && (
                      <>
                        <span>•</span>
                        <div className="flex items-center gap-1 text-slate-600" title={destinationPath}>
                          <Folder className="h-3 w-3 text-amber-500/40 fill-amber-400/15" />
                          <span className="truncate max-w-[200px]">{destinationPath}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <NewButton
                  variant="ghost"
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(virtualRow.index);
                  }}
                  disabled={isUploading || isDownloadingFromIManage}
                  icon={X}
                />
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </div>
  );
});

VirtualizedFileList.displayName = 'VirtualizedFileList';

export const FileUpload = React.forwardRef<FileUploadHandles, FileUploadProps>(({
  matterGuid,
  onUploadComplete,
  disabled = false,
  buttonText = 'Upload files',
  buttonTooltip,
  variant = 'outline',
  size = 'sm',
  acceptTypes = SUPPORTED_UPLOAD_FILE_TYPES_STRING,
  multiple = true,
  showAsDialog = false,
  dialogTriggerComponent,
  className = '',
  onProcessingStateChange,
  documentType,
  isDraft = false,
  // Customization props
  hideSelectFolderButton = false,
  selectFilesButtonText = 'Select Files',
  hideOptions = false,
  hideIManageImport = false,
  supportedTypesText = DEFAULT_SUPPORTED_TYPES_TEXT,
  dialogTitle = 'Upload files',
  dropZoneText,
  isLoading = false
}, ref) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false); // Track only blob upload phase
  const [isExtractingZip, setIsExtractingZip] = useState<boolean>(false); // Track zip extraction
  const [batchTaskId, setBatchTaskId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState<boolean>(false);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string | null>(documentType || null);
  const [isDraftMode, setIsDraftMode] = useState<boolean>(isDraft);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const processFilesRef = useRef<((files: File[], source?: string) => Promise<void>) | undefined>(undefined);
  const onUploadCompleteRef = useRef(onUploadComplete);
  const [showIManagePicker, setShowIManagePicker] = useState<boolean>(false);
  const [selectedIManageFiles, setSelectedIManageFiles] = useState<IManagePickerFile[]>([]);
  const [isFetchingMetadata, setIsFetchingMetadata] = useState<boolean>(false);
  const { isEnabled: iManageEnabled } = useFeatures(FEATURE_FLAGS.IMANAGE);
  const netDocsConfig = useNetDocsStatus();
  const [showNetDocsPicker, setShowNetDocsPicker] = useState<boolean>(false);

  // Phase 4: Folder structure detection state
  const [folderStructureInfo, setFolderStructureInfo] = useState<{
    totalFolders: number;
    uniquePaths: Set<string>;
  } | null>(null);

  const [isDownloadingFromIManage, setIsDownloadingFromIManage] = useState<boolean>(false);

  // Keep the callback ref updated with the latest callback
  useEffect(() => {
    onUploadCompleteRef.current = onUploadComplete;
  }, [onUploadComplete]);

  // Check token limits
  const { tokenLimitInfo, isLoading: isTokenLimitLoading } = useTokenLimit();

  // Background sync for upload progress
  const { triggerSync } = useBackgroundSync();

  // Get folder tree to look up folder names
  const { folderTree } = useFolderTree(matterGuid);

  // Sync selectedDocumentType when documentType prop changes (e.g., when user navigates folders)
  useEffect(() => {
    setSelectedDocumentType(documentType || null);
  }, [documentType]);

  useImperativeHandle(ref, () => ({
    openDialog: () => {
      if (showAsDialog) {
        setDialogOpen(true);
      }
    },
    openWithFiles: async (files: File[], folderId?: string) => {
      // Reset drag state to prevent visual highlighting
      setIsDraggingOver(false);

      const allFiles = await extractAndCombineFiles(files);

      // Set the files
      setSelectedFiles(allFiles);

      // If a specific folder ID is provided, use it immediately
      if (folderId) {
        setSelectedDocumentType(folderId);
      }

      // Check for folder structure and build info
      const uniquePaths = new Set<string>();
      allFiles.forEach(file => {
        const relativePath = (file as any).webkitRelativePath;
        if (relativePath && relativePath.includes('/')) {
          // Extract folder path (everything except filename)
          const pathParts = relativePath.split('/');
          const folderPath = pathParts.slice(0, -1).join('/');
          if (folderPath) {
            uniquePaths.add(folderPath);
          }
        }
      });

      if (uniquePaths.size > 0) {
        setFolderStructureInfo({
          totalFolders: uniquePaths.size,
          uniquePaths: uniquePaths
        });
      }

      // Open dialog
      if (showAsDialog) {
        setDialogOpen(true);
      }
    }
  }));

  // Function to clear state from sessionStorage
  const clearUploadState = () => {
    if (matterGuid) {
      sessionStorage.removeItem(`uploadState-${matterGuid}`);
    }
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    if (showAsDialog) {
      setDialogOpen(true);
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const closeDialog = () => {
    setShowCloseConfirmation(false);
    setDialogOpen(false);
    setSelectedFiles([]);
    setSelectedIManageFiles([]);
    setSelectedDocumentType(documentType || null);
    setFolderStructureInfo(null);
    setIsDraftMode(false);
    setIsDownloadingFromIManage(false);
    resetFileInput();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      setDialogOpen(true);
      return;
    }

    // Prevent closing during upload or iManage download
    if (isUploading || isDownloadingFromIManage || isExtractingZip) {
      return;
    }

    if (selectedFiles.length > 0 || selectedIManageFiles.length > 0) {
      setShowCloseConfirmation(true);
      return;
    }

    closeDialog();
  };

  const discardSelectedFilesAndClose = () => {
    closeDialog();
  };

  // Helper function to check if file is already in the list
  const isFileAlreadySelected = useCallback((file: File, existingFiles: File[]): boolean => {
    return existingFiles.some(
      existingFile =>
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified
    );
  }, []);

  // Phase 4: Detect folder structure from files
  const detectFolderStructure = useCallback((files: File[]) => {
    const folderPaths = new Set<string>();
    let hasFolderStructure = false;

    files.forEach(file => {
      const relativePath = (file as any).webkitRelativePath;
      if (relativePath && relativePath.includes('/')) {
        hasFolderStructure = true;
        // Extract folder path (everything except filename)
        const pathParts = relativePath.split('/');
        // Strip top-level folder and add remaining hierarchy
        // Start from index 2 to skip the top-level folder name
        for (let i = 2; i < pathParts.length; i++) {
          const folderPath = pathParts.slice(1, i).join('/');
          if (folderPath) {
            folderPaths.add(folderPath);
          }
        }
      }
    });

    if (hasFolderStructure) {
      setFolderStructureInfo({
        totalFolders: folderPaths.size,
        uniquePaths: folderPaths
      });
      console.log('[Folder Detection] Detected folder structure:', {
        totalFolders: folderPaths.size,
        paths: Array.from(folderPaths)
      });
    } else {
      setFolderStructureInfo(null);
    }
  }, []);

  // Extract zips and combine with regular files, showing loading state
  const extractAndCombineFiles = async (files: File[]): Promise<File[]> => {
    const zipFiles = files.filter(isZipFile);
    const regularFiles = files.filter(file => !isZipFile(file));

    if (zipFiles.length === 0) return regularFiles;

    setIsExtractingZip(true);
    try {
      const extracted: File[] = [];
      for (const zipFile of zipFiles) {
        extracted.push(...await extractZipFile(zipFile));
      }
      if (extracted.length > 0) {
        toast.success(`Extracted ${extracted.length} file${extracted.length > 1 ? 's' : ''} from zip`, {
          style: { zIndex: 100000 }
        });
      }
      return [...regularFiles, ...extracted];
    } finally {
      setIsExtractingZip(false);
    }
  };

  const handleFileSelection = useCallback(async (files: FileList | readonly File[] | null) => {
    if (!files || files.length === 0) return;

    // Filter files using shared utility
    const validFiles = Array.from(files).filter(file => !shouldFilterFile(file, acceptTypes));

    if (validFiles.length === 0) {
      toast.error('No supported files found in the selection');
      return; // All files were filtered out
    }

    const allFiles = await extractAndCombineFiles(validFiles);

    if (allFiles.length === 0) {
      return; // No valid files after extraction
    }

    // Detect folder structure
    detectFolderStructure(allFiles);

    // Append new files to existing selection, skipping duplicates
    setSelectedFiles(prev => {
      const newFiles = allFiles.filter(
        file => !isFileAlreadySelected(file, prev)
      );
      return [...prev, ...newFiles];
    });
  }, [acceptTypes, detectFolderStructure, isFileAlreadySelected]);

  const removeFile = (index: number) => {
    setSelectedFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // Re-detect folder structure with remaining files
      detectFolderStructure(updated);
      return updated;
    });
  };

  // Helper function to get the destination path for a file
  const getFileDestinationPath = (file: File): string | null => {
    const relativePath = (file as any).webkitRelativePath;
    // Build display path using folder names (not slugs)
    const folderPath = selectedDocumentType
      ? buildDisplayPathFromTree(selectedDocumentType, folderTree)
      : null;

    return getFileDestinationPathUtil(folderPath, relativePath);
  };

  // Check if any files are orphaned (no parent folder and no dropdown folder selected)
  const hasOrphanedFiles = useMemo(() => {
    if (selectedFiles.length === 0) return false;
    if (selectedDocumentType) return false; // If dropdown folder is selected, no files are orphaned

    return selectedFiles.some(file => {
      const relativePath = (file as any).webkitRelativePath;
      // File is orphaned if it has no webkitRelativePath or path doesn't contain '/'
      return !relativePath || !relativePath.includes('/');
    });
  }, [selectedFiles, selectedDocumentType]);

  // Calculate total upload size and check against limit
  const MAX_UPLOAD_SIZE_BYTES = 3 * 1024 * 1024 * 1024; // 3 GB
  const { totalSizeBytes, totalSizeFormatted, sizePercentage, isOverSizeLimit } = useMemo(() => {
    const totalBytes = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const percentage = Math.min((totalBytes / MAX_UPLOAD_SIZE_BYTES) * 100, 100);

    // Format size for display
    let formatted: string;
    if (totalBytes >= 1024 * 1024 * 1024) {
      formatted = `${(totalBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    } else if (totalBytes >= 1024 * 1024) {
      formatted = `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
    } else if (totalBytes >= 1024) {
      formatted = `${(totalBytes / 1024).toFixed(0)} KB`;
    } else {
      formatted = `${totalBytes} B`;
    }

    return {
      totalSizeBytes: totalBytes,
      totalSizeFormatted: formatted,
      sizePercentage: percentage,
      isOverSizeLimit: totalBytes > MAX_UPLOAD_SIZE_BYTES
    };
  }, [selectedFiles]);

  // Handle files selected from iManage
  const handleIManageFilesSelected = async (files: any) => {

    // Parse the files - they come as a comma-separated string like "DEV!7.1,DEV!6.1,DEV!5.1"
    let fileArray: string[] = [];
    if (typeof files === 'string') {
      fileArray = files.split(',').filter(f => f.trim());
    } else if (Array.isArray(files)) {
      fileArray = files;
    }

    // Set loading state
    setIsFetchingMetadata(true);

    // Get metadata for each file to show proper names
    try {
      // Get auth data from session
      const authData = JSON.parse(sessionStorage.getItem('imanage_auth') || '{}');

      if (authData.access_token && authData.customer_id) {
        const response = await backendClient.post('/imanage/get-documents-metadata', {
          access_token: authData.access_token,
          customer_id: authData.customer_id,
          user_id: authData.user_id,
          documents: fileArray
        });

        // Store files with ID, name, and extension
        const filesWithMetadata = response.data.documents.map((doc: any) => ({
          id: doc.id,
          name: doc.name,
          extension: doc.extension || '' // Store the extension
        }));

        // Merge with existing selections, avoiding duplicates
        setSelectedIManageFiles(prevFiles => {
          const existingIds = new Set(prevFiles.map(f => f.id));
          const newFiles = filesWithMetadata.filter((file: any) => !existingIds.has(file.id));
          return [...prevFiles, ...newFiles];
        });
      }
    } catch (error) {
      console.error('Failed to get file metadata:', error);
    } finally {
      setIsFetchingMetadata(false);
    }

    setShowIManagePicker(false);

    // Don't close the dialog - keep it open so user can see the files and click Upload
  };

  // Handle files downloaded from NetDocuments (already File objects)
  const handleNetDocsFilesDownloaded = useCallback((files: File[]) => {
    if (files.length === 0) return;

    // Add to selected files list just like local file selection
    setSelectedFiles(prev => {
      const newFiles = files.filter(file => !isFileAlreadySelected(file, prev));
      return [...prev, ...newFiles];
    });
  }, [isFileAlreadySelected]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // If showAsDialog is false, process files immediately
    if (!showAsDialog) {
      await processFiles(Array.from(files));
    } else {

      handleFileSelection(files);

      // Reset the file input after selection
      resetFileInput();
    }
  };

  const processFilesWithIManage = async (localFiles: File[], iManageFiles: IManagePickerFile[]) => {
    if (localFiles.length === 0 && iManageFiles.length === 0) return;
    if (!matterGuid) return;

    // Check if files have folder structure
    const hasOwnFolderStructure = localFiles.some(file => {
      const relativePath = (file as any).webkitRelativePath;
      return relativePath && relativePath.includes('/');
    });

    // Validate document type is selected (unless it's pre-set via props or files have their own folder structure)
    if (!selectedDocumentType && !documentType && !hasOwnFolderStructure) {
      toast.error('Please select a folder before uploading files');
      return;
    }

    try {
      setIsProcessing(true);

      // Notify parent component about processing state
      if (onProcessingStateChange) {
        onProcessingStateChange(true);
      }

      const allFiles: File[] = [...localFiles];

      // Download iManage files if any
      if (iManageFiles.length > 0) {
        // Set downloading state to show "Downloading..." in button
        setIsDownloadingFromIManage(true);

        // Get auth data from session
        const authData = JSON.parse(sessionStorage.getItem('imanage_auth') || '{}');

        if (!authData.access_token || !authData.customer_id) {
          throw new Error('iManage authentication required. Please re-authenticate.');
        }

        // Download documents from iManage
        const downloadResponse = await backendClient.post('/imanage/download-documents', {
          access_token: authData.access_token,
          customer_id: authData.customer_id,
          documents: iManageFiles.map(f => f.id)
        });

        const metadataById = new Map(iManageFiles.map((file) => [file.id, file]));

        // Convert downloaded files to File objects
        for (const downloadedFile of downloadResponse.data.files) {
          // Decode base64 content if necessary
          let fileContent = downloadedFile.data;

          // If content is base64 encoded, decode it
          if (typeof fileContent === 'string') {
            const byteCharacters = atob(fileContent);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            fileContent = byteArray;
          }

          // Name from iManage profile (name + extension), not Content-Disposition.
          const file = new File(
            [fileContent],
            imanagePickerUploadFilename(metadataById.get(downloadedFile.id), downloadedFile.filename),
            { type: downloadedFile.content_type || 'application/octet-stream' }
          );

          allFiles.push(file);
        }

        // Done downloading, turn off download state
        setIsDownloadingFromIManage(false);
      }

      // Now process all files (local + downloaded iManage files).
      // Manual iframe imports are user uploads into Crimson, not iManage sync records.
      const uploadSource = 'crimson';
      await processFiles(allFiles, uploadSource);

      // Clear the selections after successful upload
      setSelectedFiles([]);
      setSelectedIManageFiles([]);

    } catch (error: any) {
      console.error('Failed to process files:', error);
      toast.error(error.message || 'Failed to process files');
      setIsProcessing(false);
      setIsDownloadingFromIManage(false);
      clearUploadState();
      if (onProcessingStateChange) {
        onProcessingStateChange(false);
      }
    }
  };

  const processFiles = async (files: File[], source: string = 'crimson') => {
    if (files.length === 0 || !matterGuid) return;

    // Check if files have folder structure
    const hasOwnFolderStructure = files.some(file => {
      const relativePath = (file as any).webkitRelativePath;
      return relativePath && relativePath.includes('/');
    });

    // Validate document type is selected (unless it's pre-set via props or files have their own folder structure)
    if (!selectedDocumentType && !documentType && !hasOwnFolderStructure) {
      toast.error('Please select a folder before uploading files');
      return;
    }

    try {
      setIsProcessing(true);
      setIsUploading(true);

      // Notify parent component about processing state
      if (onProcessingStateChange) {
        onProcessingStateChange(true);
      }

      // Use orchestrateUpload to handle uploading and backend queueing
      // Progress will be tracked in the database and polled by UploadProgressCenter
      const uploadPromise = orchestrateUpload({
        matterGuid,
        files,
        documentType: selectedDocumentType,
        isDraft: isDraftMode,
        source,
        // Trigger immediate sync right after batch is created in DB
        onBatchCreated: async () => {
          // The upload has already been accepted, so bypass the confirmation
          // reserved for user-initiated dismissals.
          if (showAsDialog) {
            closeDialog();
          }
          await triggerSync('file-upload-progress');
        }
      });

      const result = await uploadPromise;
      const { batchTaskId: batch_task_id, blobNames: allBlobNames, rejectedFiles } = result;

      if (rejectedFiles.length > 0) {
        toast.error(
          rejectedFiles.length === 1
            ? `Failed to upload ${rejectedFiles[0]}`
            : `Failed to upload ${rejectedFiles.length} files`
        );
      }

      // If no files were uploaded, stop processing
      if (allBlobNames.length === 0) {
        console.log('No files to upload, stopping upload process');
        setIsProcessing(false);
        setIsUploading(false);
        clearUploadState();
        if (onProcessingStateChange) {
          onProcessingStateChange(false);
        }
        return;
      }

      // Set batch task ID for UI tracking
      if (batch_task_id) {
        setBatchTaskId(batch_task_id);
      }

      // Trigger immediate background sync to show backend batch in UploadProgressCenter
      await triggerSync('file-upload-progress');

      // UploadProgressCenter will automatically poll for batch status from database
      // No need for client-side state management anymore

      setIsUploading(false);

      // Files are uploaded - backend will process automatically
      // UploadProgressCenter will show progress by polling /upload-batches endpoint

    } catch (error: any) {
      console.error('Error uploading files:', error);
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : error?.message || 'Failed to upload files');
      setIsProcessing(false);
      setIsUploading(false);
      clearUploadState();

      // Notify parent component about processing state
      if (onProcessingStateChange) {
        onProcessingStateChange(false);
      }
    }
  };

  // Keep ref updated with latest processFiles function
  useEffect(() => {
    processFilesRef.current = processFiles;
  });

  const uploadFiles = async () => {
    if (selectedFiles.length === 0 && selectedIManageFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    if (!matterGuid) return;

    // Process both local files and iManage files
    // Dialog will be closed inside processFiles when upload starts
    await processFilesWithIManage(selectedFiles, selectedIManageFiles);
  };

  // Polling is now handled by UploadProgressCenter via useUploadProgress hook
  // which polls the new /upload-batches endpoint automatically

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Add window-level drag and drop handlers - AFTER all functions are defined
  useEffect(() => {
    if (disabled) return;

    // Track drag counter to handle nested elements
    let dragCounter = 0;

    const handleWindowDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounter++;

      // Only highlight the drop zone if actually dragging files
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDraggingOver(true);
      }
    };

    const handleWindowDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Ensure dragging state is maintained
      if (e.dataTransfer?.types.includes('Files') && !isDraggingOver) {
        setIsDraggingOver(true);
      }

      // Make sure the dataTransfer has the right properties for accepting files
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleWindowDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      dragCounter--;

      // Only turn off dragging when all drag events are complete
      if (dragCounter === 0) {
        setIsDraggingOver(false);
      }
    };

    const handleWindowDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Reset drag counter and visual state
      dragCounter = 0;
      setIsDraggingOver(false);

      // Don't allow drop if iManage files are selected
      if (selectedIManageFiles.length > 0) {
        toast.error('Clear iManage files first to upload local files');
        return;
      }

      // Check if in dialog mode and dialog is open, or if not in dialog mode
      const shouldProcessDrop = !showAsDialog || (showAsDialog && dialogOpen);

      if (!shouldProcessDrop) return;

      try {
        if (!e.dataTransfer) return;
        const files = await processDroppedDataTransfer(e.dataTransfer);
        if (files.length === 0) {
          toast.error('No files could be read from the drop. Try selecting them using the upload button.');
          return;
        }

        // Process files immediately or add to selection
        if (!showAsDialog && processFilesRef.current) {
          await processFilesRef.current(files);
        } else {
          await handleFileSelection(files);
        }
      } catch (error) {
        console.error('Error processing dropped files:', error);
        toast.error('Failed to read the dropped files. Please try again.');
      }
    };

    // Add an extra event to detect the start of a drag operation
    document.addEventListener('dragenter', handleWindowDragEnter);
    document.addEventListener('dragover', handleWindowDragOver);
    document.addEventListener('dragleave', handleWindowDragLeave);
    document.addEventListener('drop', handleWindowDrop);

    return () => {
      document.removeEventListener('dragenter', handleWindowDragEnter);
      document.removeEventListener('dragover', handleWindowDragOver);
      document.removeEventListener('dragleave', handleWindowDragLeave);
      document.removeEventListener('drop', handleWindowDrop);
    };
  }, [showAsDialog, dialogOpen, disabled, isDraggingOver, handleFileSelection, selectedIManageFiles.length]);

  // Check if disabled due to token limit or loading
  const isDisabledByTokenLimit = !tokenLimitInfo.isWithinLimit;
  const isEffectivelyDisabled = disabled || isDisabledByTokenLimit || isTokenLimitLoading || isUploading || isExtractingZip || isLoading;

  // Default upload button.
  const uploadLabel = buttonTooltip || buttonText;
  const uploadButtonInner = (
    <Button
      variant={variant}
      size={size}
      onClick={handleButtonClick}
      disabled={isEffectivelyDisabled}
      className={`flex items-center gap-2 ${className}`}
      aria-label={uploadLabel}
    >
      <Upload className="h-4 w-4" />
      {buttonText ? <span className={toolbarActionLabelClass}>{buttonText}</span> : null}
    </Button>
  );

  // Tooltip stays available when the label is hidden by the toolbar container query.
  const uploadButton = (
    <TokenLimitTooltip
      message={tokenLimitInfo.message}
      disabled={isDisabledByTokenLimit}
    >
      {uploadLabel ? (
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>{uploadButtonInner}</TooltipTrigger>
            <TooltipContent side="top" className="text-xs font-normal">
              {uploadLabel}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        uploadButtonInner
      )}
    </TokenLimitTooltip>
  );


  return (
    <>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept={acceptTypes}
        multiple={multiple}
        disabled={disabled}
      />

      {/* Render as dialog or simple button */}
      {showAsDialog ? (
        <>
          <div className="relative">
            {/* Dialog trigger - either custom component or default button */}
            {dialogTriggerComponent ? (
              <TokenLimitTooltip
                message={tokenLimitInfo.message}
                disabled={isDisabledByTokenLimit}
              >
                <div onClick={isEffectivelyDisabled ? undefined : handleButtonClick}>
                  {dialogTriggerComponent}
                </div>
              </TokenLimitTooltip>
            ) : (
              uploadButton
            )}
          </div>

          {/* Upload dialog */}
          <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogContent
              className={`sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-visible !p-4 !gap-3 !flex flex-col transition-[filter] duration-150 ${showCloseConfirmation ? 'brightness-[0.65] pointer-events-none' : ''}`}
              hideCloseButton={isUploading || isDownloadingFromIManage || isExtractingZip}
              onPointerDownOutside={(e) => {
                // Prevent closing when clicking outside during upload or download
                if (isUploading || isDownloadingFromIManage || isExtractingZip) {
                  e.preventDefault();
                }
              }}
              onEscapeKeyDown={(e) => {
                // Prevent closing with ESC during upload or download
                if (isUploading || isDownloadingFromIManage || isExtractingZip) {
                  e.preventDefault();
                }
              }}
              onInteractOutside={(e) => {
                // Prevent any outside interaction during upload or download
                if (isUploading || isDownloadingFromIManage || isExtractingZip) {
                  e.preventDefault();
                }
              }}
            >
              <DialogHeader className="shrink-0 pb-3">
                <DialogTitle>{dialogTitle}</DialogTitle>
              </DialogHeader>

              {/* File selection area */}
              <div className="min-h-0 flex-1 overflow-y-auto flex flex-col gap-3 px-1">
                <div className="space-y-3 shrink-0">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Folder</label>
                    <FolderPicker
                      matterSlug={matterGuid}
                      value={selectedDocumentType}
                      onChange={setSelectedDocumentType}
                      disabled={isUploading || isDownloadingFromIManage || isExtractingZip}
                      allowCreateFolder
                    />
                  </div>

                  {!hideOptions && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Switch
                          id="draft-mode"
                          checked={isDraftMode}
                          onCheckedChange={setIsDraftMode}
                          disabled={isUploading || isDownloadingFromIManage || isExtractingZip}
                        />
                        <div className="flex flex-col">
                          <Label htmlFor="draft-mode" className={`flex items-center gap-2 ${isUploading || isDownloadingFromIManage || isExtractingZip ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                            <FileEdit className="h-4 w-4 text-blue-500" />
                            <span>File as drafts</span>
                          </Label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <FileUploadDropArea
                  ref={dropZoneRef}
                  disabled={selectedIManageFiles.length > 0 || isUploading || isDownloadingFromIManage || isExtractingZip}
                  disabledText={
                    isUploading || isDownloadingFromIManage || isExtractingZip
                      ? 'Uploading in progress...'
                      : 'Clear iManage files to upload local files'
                  }
                  dropZoneText={dropZoneText || (hideSelectFolderButton ? 'Drag files here' : 'Drag files or folders here')}
                  isDraggingOver={isDraggingOver}
                  onSelectFiles={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.removeAttribute('webkitdirectory');
                      fileInputRef.current.removeAttribute('directory');
                      fileInputRef.current.click();
                    }
                  }}
                  onSelectFolder={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.setAttribute('webkitdirectory', '');
                      fileInputRef.current.setAttribute('directory', '');
                      fileInputRef.current.click();
                    }
                  }}
                  selectFilesButtonText={selectFilesButtonText}
                  showSelectFolderButton={!hideSelectFolderButton}
                >
                  <SupportedFileTypesText
                    text={supportedTypesText}
                    acceptTypes={acceptTypes}
                    className="mt-2 text-xs text-slate-500 max-sm:hidden [@media(max-height:720px)]:hidden"
                  />
                </FileUploadDropArea>

                {/* Loading indicator for metadata fetching */}
                {isFetchingMetadata && (
                  <div className="shrink-0 p-3 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Loading text='Fetching files from iManage...'/>
                  </div>
                )}

                {/* Selected files list */}
                {(selectedFiles.length > 0 || selectedIManageFiles.length > 0) && !isFetchingMetadata && (
                  <div className="min-h-0 flex-1 flex flex-col">
                    <div className="flex justify-between items-center mb-2 shrink-0">
                      <h4 className="text-sm font-medium">Selected Files ({selectedFiles.length + selectedIManageFiles.length})</h4>
                      <NewButton
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-slate-500 hover:text-slate-700"
                        onClick={() => {
                          setSelectedFiles([]);
                          setSelectedIManageFiles([]);
                          setFolderStructureInfo(null); // Clear folder detection
                        }}
                        disabled={isUploading || isDownloadingFromIManage || isExtractingZip}
                        text='Clear All'
                      />
                    </div>

                    {/* Upload size indicator */}
                    {selectedFiles.length > 0 && (
                      <div className="mb-2 shrink-0">
                        <div className="flex justify-between items-center text-xs mb-1">
                          <span className={isOverSizeLimit ? 'text-red-600 font-medium' : 'text-slate-600'}>
                            {totalSizeFormatted} / 3 GB
                          </span>
                          {isOverSizeLimit && (
                            <span className="text-red-600 font-medium">Exceeds limit</span>
                          )}
                        </div>
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${
                              isOverSizeLimit ? 'bg-red-500' : sizePercentage > 80 ? 'bg-yellow-500' : 'bg-blue-500'
                            }`}
                            style={{ width: `${Math.min(sizePercentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="border border-slate-200 rounded-md min-h-0 shrink">
                      {selectedFiles.length > 0 && (
                        <VirtualizedFileList
                          files={selectedFiles}
                          getFileDestinationPath={getFileDestinationPath}
                          removeFile={removeFile}
                          isUploading={isUploading}
                          isDownloadingFromIManage={isDownloadingFromIManage}
                        />
                      )}

                      {/* Display iManage files (separate non-virtualized list) */}
                      {selectedIManageFiles.length > 0 && (
                        <ScrollArea className="h-[clamp(6rem,22dvh,12.5rem)]">
                          <div className="p-1.5 space-y-1">
                            {selectedIManageFiles.map((file, index) => (
                              <div
                                key={`imanage-${file.id}-${index}`}
                                className="flex items-center text-sm px-1.5 py-1 bg-blue-50 rounded-sm group"
                                title={`iManage: ${file.name} (${file.id})`}
                              >
                                <FileText className="h-4 w-4 mr-2 text-blue-500 shrink-0" />
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="truncate w-full font-medium">
                                    {(() => {
                                      const fullName = joinIManageDocumentFilename(file.name, file.extension);
                                      return fullName.length > 40
                                        ? fullName.substring(0, 25) + '...' + (fullName.includes('.') ? fullName.substring(fullName.lastIndexOf('.')) : '')
                                        : fullName;
                                    })()}
                                  </span>
                                  <div className="flex items-center text-xs text-blue-600">
                                    <span>iManage</span>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0 opacity-80 group-hover:opacity-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedIManageFiles(prev => prev.filter((_, i) => i !== index));
                                  }}
                                  disabled={isUploading || isDownloadingFromIManage || isExtractingZip}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                    </div>

                    {/* Error message for orphaned files */}
                    {hasOrphanedFiles && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-2 shrink-0">
                        <svg className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-yellow-800">Destination folder required</p>
                          <p className="text-xs text-yellow-700 mt-1">
                            Some files have no parent folder. Please select a destination folder from the dropdown above.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter className="shrink-0 pt-3 border-t border-slate-100">
                <div className="flex items-end w-full">
                  {((!hideIManageImport && iManageEnabled) || netDocsConfig.enabled) && (
                    <div className="flex flex-col gap-2 w-[165px]">
                      {!hideIManageImport && iManageEnabled && (
                        <Button
                          variant="outline"
                          onClick={() => setShowIManagePicker(true)}
                          disabled={isFetchingMetadata || selectedFiles.length > 0 || isUploading || isExtractingZip}
                          className="flex items-center gap-2"
                          title={selectedFiles.length > 0 ? "Clear local files first to import from iManage" : ""}
                        >
                          <img
                            src="/iManage-logo.png"
                            alt="iManage"
                            className="h-4 w-auto"
                          />
                          iManage
                        </Button>
                      )}
                      {netDocsConfig.enabled && (
                        <Button
                          variant="outline"
                          onClick={() => setShowNetDocsPicker(true)}
                          disabled={isFetchingMetadata || selectedFiles.length > 0 || selectedIManageFiles.length > 0 || isUploading || isExtractingZip}
                          className="flex items-center gap-2"
                          title={selectedFiles.length > 0 ? "Clear local files first to import from NetDocuments" : selectedIManageFiles.length > 0 ? "Clear iManage files first to import from NetDocuments" : ""}
                        >
                          <img
                            src="/netdocs-logo.svg"
                            alt="NetDocuments"
                            className="h-4 w-auto"
                          />
                          NetDocuments
                        </Button>
                      )}
                    </div>
                  )}
                  <Button
                    onClick={uploadFiles}
                    disabled={isUploading || isDownloadingFromIManage || isExtractingZip || isOverSizeLimit || (selectedFiles.length === 0 && selectedIManageFiles.length === 0) || (!selectedDocumentType && !folderStructureInfo) || hasOrphanedFiles}
                    className="w-[165px] ml-auto"
                  >
                    {isDownloadingFromIManage ? (
                      <Loading text='Downloading' inline size='sm' />
                    ) : isExtractingZip ? (
                      <Loading text='Extracting' inline size='sm' />
                    ) : isUploading ? (
                      <Loading text='Uploading' inline size='sm' />
                    ) : (
                      'Upload'
                    )}
                  </Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showCloseConfirmation} onOpenChange={setShowCloseConfirmation}>
            <DialogContent className="z-[100000] sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>Close upload dialog?</DialogTitle>
                <DialogDescription>
                  The {selectedFiles.length + selectedIManageFiles.length} selected{' '}
                  {(selectedFiles.length + selectedIManageFiles.length) === 1 ? 'file' : 'files'} will not be uploaded.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <NewButton
                  variant="outline"
                  onClick={() => setShowCloseConfirmation(false)}
                  text="Continue uploading"
                />
                <NewButton
                  variant="destructive"
                  onClick={discardSelectedFilesAndClose}
                  text="Discard files"
                />
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div style={{ minHeight: isProcessing ? '50px' : 'auto' }} className="relative">
          <div className="relative z-10">
            {uploadButton}
          </div>

          {/* Upload progress indicator */}
          {(isUploading || isExtractingZip) && (
            <div className="absolute -bottom-8 left-0 right-0 flex items-center gap-2 text-xs text-muted-foreground">
              <Loading text='Preparing files...'/>
            </div>
          )}
        </div>
      )}

      {/* iManage File Picker - only render if enabled */}
      {iManageEnabled && (
        <IManageFilePicker
          isOpen={showIManagePicker}
          onClose={() => setShowIManagePicker(false)}
          onFilesSelected={handleIManageFilesSelected}
          matterId={matterGuid}
        />
      )}

      {/* NetDocuments File Picker - only render if enabled */}
      {netDocsConfig.enabled && (
        <NetDocsFilePicker
          isOpen={showNetDocsPicker}
          onClose={() => setShowNetDocsPicker(false)}
          onFilesDownloaded={handleNetDocsFilesDownloaded}
          accessKey={netDocsConfig.accessKey}
          region={netDocsConfig.region}
          stage={netDocsConfig.stage}
        />
      )}
    </>
  );
});

FileUpload.displayName = 'FileUpload';
